import React, { useEffect, useState, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { FiPhoneCall, FiSearch } from "react-icons/fi";
import { callbacks, callLogs, campaigns, contacts, dialKeys } from "../ctiData";
import { formatSeconds, toneStyle } from "../ctiHelpers";
import { FaPause, FaMicrophone, FaPhone } from "react-icons/fa";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FiInfo } from "react-icons/fi";
import { FaCheck, FaTimes } from "react-icons/fa";

function DeleteGlyph() {
  return <span className="delete-fallback">⌫</span>;
}

function IdleScreen({
  idleSeconds,
  idleStatus,
  activeTab,
  dialNum,
  breakEndTime,
  countdown,
  breaksData,
  selectedBreakId,
  onDial,
  onDialBack,
  onTabChange,
  onStatusChange,
  onPlaceCall,
  onSetBreakClick,
  onRemoveBreakClick,
}) {
  const idleStatusLabel = idleStatus === "BREAK" ? "ON BREAK" : idleStatus;
  const [webrtcStatus, setWebrtcStatus] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      const status = sessionStorage.getItem("webrtcPhoneStatus");
      setWebrtcStatus(status);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const parsedStatus = useMemo(() => {
    if (!webrtcStatus)
      return {
        success: false,
        code: "DISCONNECTED",
        message: "Dialer is Disconnected",
      };

    if (!webrtcStatus.includes("-")) {
      return {
        success: true,
        code: "READY",
        message: "Dialer connected",
      };
    }

    const [code, message] = webrtcStatus.split("-");

    return {
      success: false,
      code: code || "ERROR",
      message: message || "Connection error",
    };
  }, [webrtcStatus]);

  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      {parsedStatus ? parsedStatus?.message : null}
    </Tooltip>
  );

  const [breakRemaining, setBreakRemaining] = useState(null);

  useEffect(() => {
    if (!breakEndTime) return;

    const timer = setInterval(() => {
      const remaining = Math.floor((breakEndTime - Date.now()) / 1000);

      if (remaining <= 0) {
        setBreakRemaining(0);
        clearInterval(timer);
      } else {
        setBreakRemaining(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [breakEndTime]);

  const activeBreak = useMemo(() => {
    return breaksData?.find((b) => b.id == selectedBreakId);
  }, [breaksData, selectedBreakId]);

  return (
    <div className="screen active">
      <div className="idle-grid">
        <div className="stack-col">
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Today's Stats</span>
            </div>
            <div className="panel-body">
              <div className="idle-timer-box">
                <div className="idle-timer-label">Idle Duration</div>
                <div className="idle-timer-value">
                  {formatSeconds(idleSeconds)}
                </div>
                <div className="idle-timer-sub">Since last call ended</div>
              </div>
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">Calls Today</div>
                  <div className="stat-value">47</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Talk Time</div>
                  <div className="stat-value amber">3h 12m</div>
                </div>
              </div>
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">Connected</div>
                  <div className="stat-value green">31</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Avg Handle</div>
                  <div className="stat-value">4m 05s</div>
                </div>
              </div>
              <div className="status-action-row">
                {idleStatus !== "BREAK" && (
                  <Button
                    className="status-action amber-btn"
                    onClick={onSetBreakClick}
                  >
                    SET BREAK
                  </Button>
                )}

                {idleStatus === "BREAK" && (
                  <div
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "#fff3cd",
                      borderRadius: "8px",
                      border: "1px solid #ffeeba",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {activeBreak?.title || "Break"} Active
                    </div>

                    <div
                      style={{
                        fontSize: "18px",
                        marginTop: "6px",
                        fontWeight: 700,
                      }}
                    >
                      {formatSeconds(breakRemaining || countdown || 0)}
                    </div>

                    <Button
                      className="status-action red-btn"
                      style={{ marginTop: "10px" }}
                      onClick={onRemoveBreakClick}
                    >
                      CANCEL BREAK
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="panel fill-panel">
            <div className="panel-header">
              <span className="panel-title">Active Campaigns</span>
              <span className="mono-green">2 LIVE</span>
            </div>
            <div className="panel-body">
              {campaigns.map((campaign) => (
                <div className="campaign-item" key={campaign.name}>
                  <div
                    className="campaign-dot"
                    style={{ background: campaign.color }}
                  />
                  <div className="flex-1">
                    <div className="campaign-name">{campaign.name}</div>
                    <div className="campaign-meta">{campaign.meta}</div>
                  </div>
                  <span className={`campaign-badge ${campaign.badgeClass}`}>
                    {campaign.badge}
                  </span>
                </div>
              ))}
              <div className="queue-card">
                <div className="queue-title">Queue Status</div>
                <div className="queue-row">
                  <span>Waiting:</span>
                  <span className="queue-amber">3 calls</span>
                </div>
                <div className="queue-row">
                  <span>Agents Online:</span>
                  <span className="queue-green">12 / 15</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="stack-col">
          <div className="panel shrink-panel">
            <div className="panel-header">
              <span className="panel-title">WEBRTC Dialer</span>
              <OverlayTrigger placement="top" overlay={renderTooltip}>
                <span
                  className={`status-pill ${
                    !parsedStatus || !parsedStatus?.success ? "away" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                    color: parsedStatus?.success ? "green" : "red",
                  }}
                >
                  <FiInfo size={12} />

                  {parsedStatus?.success ? (
                    <FaCheck size={12} />
                  ) : (
                    <FaTimes size={12} />
                  )}
                </span>
              </OverlayTrigger>
            </div>
            <div className="panel-body tight-body">
              <div className="dialer-input-wrap">
                <Form.Control
                  className="dialer-input"
                  value={dialNum}
                  placeholder="+91 · · · · · · · · · ·"
                  readOnly
                />
                <button
                  type="button"
                  className="dial-clear"
                  onClick={onDialBack}
                >
                  <DeleteGlyph />
                </button>
              </div>
              <div className="dialpad">
                {dialKeys.map(([num, sub]) => (
                  <button
                    key={num}
                    type="button"
                    className="dial-key"
                    onClick={() => onDial(num)}
                  >
                    <span className="dial-key-num">{num}</span>
                    <span className="dial-key-sub">{sub || "\u00A0"}</span>
                  </button>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  onClick={
                    dialNum && parsedStatus?.success ? onPlaceCall : undefined
                  }
                  className="call answer"
                  id="call"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    background:
                      dialNum && parsedStatus?.success
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : "#9ca3af",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor:
                      dialNum && parsedStatus?.success
                        ? "pointer"
                        : "not-allowed",
                    boxShadow:
                      dialNum && parsedStatus?.success
                        ? "0 6px 20px rgba(16, 185, 129, 0.4), 0 0 0 4px rgba(16, 185, 129, 0.1)"
                        : "none",
                    transition: "all 0.3s ease",
                    border: "none",
                    opacity: dialNum && parsedStatus?.success ? 1 : 0.6,
                    pointerEvents:
                      dialNum && parsedStatus?.success ? "auto" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!(dialNum && parsedStatus?.success)) return;
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(16, 185, 129, 0.5), 0 0 0 6px rgba(16, 185, 129, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    if (!(dialNum && parsedStatus?.success)) return;
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(16, 185, 129, 0.4), 0 0 0 4px rgba(16, 185, 129, 0.1)";
                  }}
                >
                  <FaPhone style={{ fontSize: "18px", color: "white" }} />
                </div>
              </div>
              {/* <Button className="dial-call-btn" onClick={onPlaceCall}>
                <FiPhoneCall />
                PLACE CALL
              </Button> */}
            </div>
          </div>

          <div className="panel fill-panel">
            <div className="tab-bar">
              {[
                ["log", "Call Log"],
                ["contacts", "Phonebook"],
                ["schedule", "Callbacks"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`tab ${activeTab === key ? "active" : ""}`}
                  onClick={() => onTabChange(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div
              className={`tab-content ${activeTab === "log" ? "active" : ""}`}
            >
              {callLogs.map((item) => (
                <div className="log-item" key={`${item.name}-${item.time}`}>
                  <div className="log-icon" style={{ color: item.color }}>
                    {item.icon}
                  </div>
                  <div className="flex-1">
                    <div className="log-name">{item.name}</div>
                    <div className="log-num">{item.num}</div>
                  </div>
                  <div className="text-right">
                    <div
                      className="log-dur"
                      style={item.danger ? { color: "var(--red)" } : undefined}
                    >
                      {item.dur}
                    </div>
                    <div className="log-time">{item.time}</div>
                  </div>
                </div>
              ))}
            </div>
            <div
              className={`tab-content ${activeTab === "contacts" ? "active" : ""}`}
            >
              <div className="search-wrap">
                <FiSearch className="search-icon" />
                <input
                  className="search-bar"
                  placeholder="Search name or number..."
                />
              </div>
              {contacts.map((contact) => (
                <div className="contact-item" key={contact.name}>
                  <div className="cti-avatar" style={toneStyle(contact.tone)}>
                    {contact.initials}
                  </div>
                  <div className="flex-1">
                    <div className="contact-name">{contact.name}</div>
                    <div className="contact-num">{contact.num}</div>
                  </div>
                  <Button className="call-mini-btn">CALL</Button>
                </div>
              ))}
            </div>
            <div
              className={`tab-content ${activeTab === "schedule" ? "active" : ""}`}
            >
              {callbacks.map((item) => (
                <div className="log-item" key={item.name}>
                  <div className="log-icon" style={{ color: item.color }}>
                    {item.icon}
                  </div>
                  <div className="flex-1">
                    <div className="log-name">{item.name}</div>
                    <div className="log-num">{item.due}</div>
                  </div>
                  <Button className="call-mini-btn slim-mini-btn">CALL</Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="stack-col">
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Announcements</span>
              <span className="alert-chip">2 NEW</span>
            </div>
            <div className="panel-body tiny-pad">
              <div className="announce-card amber-bg">
                <div className="announce-tag amber-text">Script Update</div>
                <div className="announce-copy">
                  Updated objection handling guide for mortgage product
                  launched. Review before next call.
                </div>
              </div>
              <div className="announce-card blue-bg">
                <div className="announce-tag blue-text">System</div>
                <div className="announce-copy">
                  Predictive dial ratio increased to 1.8x. Expect faster call
                  connections.
                </div>
              </div>
            </div>
          </div>

          <div className="panel fill-panel">
            <div className="panel-header">
              <span className="panel-title">Today's Performance</span>
            </div>
            <div className="panel-body perf-body">
              {[
                ["Talk Time Goal", "78%", "var(--green)", "78%"],
                ["Conversion Rate", "42%", "var(--amber)", "42%"],
                ["Calls Handled", "94%", "var(--blue)", "94%"],
              ].map(([label, value, color, width]) => (
                <div className="perf-block" key={label}>
                  <div className="perf-row">
                    <span>{label}</span>
                    <span style={{ color }}>{value}</span>
                  </div>
                  <div className="prog-track">
                    <div
                      className="prog-fill"
                      style={{ width, background: color }}
                    />
                  </div>
                </div>
              ))}
              <div className="leaderboard-box">
                <div className="leaderboard-label">Leaderboard Rank</div>
                <div className="leaderboard-rank">#3</div>
                <div className="leaderboard-sub">of 12 agents today</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(IdleScreen);
